/* eslint-disable react-refresh/only-export-components */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import type { User } from '../types';
import { authApi, webauthnApi } from '../api';
import type { ConsentFields, RegisterPayload } from '../api/auth';

type OauthProvider = 'google';

// What `login` resolves to: either a live session, or a pending phishing-
// resistant second factor the caller must complete via `completeMfa`.
export type LoginOutcome =
  | { status: 'ok' }
  | {
      status: 'mfa_required';
      mfaToken: string;
      options: PublicKeyCredentialRequestOptionsJSON;
    };

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean, captchaToken?: string) => Promise<LoginOutcome>;
  completeMfa: (mfaToken: string, options: PublicKeyCredentialRequestOptionsJSON, rememberMe?: boolean) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  oauthSignIn: (provider: OauthProvider, token: string, consent?: ConsentFields) => Promise<void>;
  requestMagicLink: (email: string, captchaToken?: string) => Promise<void>;
  consumeMagicLink: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Readable hint cookie set by the API alongside the httpOnly session (see
// services/authCookies.ts). It carries no token — its mere presence tells us a
// session probably exists. Logged-out visitors don't have it, so we can skip the
// /auth/me probe and avoid the 401 noise it logs on public pages.
const SESSION_HINT_COOKIE = 'gooey_session';

function hasSessionHint(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split(';')
    .some((c) => c.trim().startsWith(`${SESSION_HINT_COOKIE}=`));
}

// Drop a stale hint (e.g. the session expired server-side) so the next page
// load doesn't keep probing /auth/me only to 401. The API also clears it on an
// explicit logout; this self-heals the expired-but-hinted case.
function clearSessionHint(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${SESSION_HINT_COOKIE}=; path=/; max-age=0`;
}

// Deliberately longer than any refresh-token TTL: an over-long hint just costs
// one 401 probe (which then self-heals via clearSessionHint), while a missing
// hint silently logs a valid session out on reload.
const SESSION_HINT_MAX_AGE_SECONDS = 60 * 24 * 60 * 60;

// Mirror the hint from the app's own origin on every session-establishing
// action. The API sets its copy too, but in prod that comes from
// api.<domain> — a host-only cookie the SPA on www.<domain> can't read via
// document.cookie, so a hard reload skipped the /auth/me probe and treated a
// perfectly valid session as logged out.
function writeSessionHint(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${SESSION_HINT_COOKIE}=1; path=/; max-age=${SESSION_HINT_MAX_AGE_SECONDS}`;
}

// Tokens live in httpOnly cookies that JS can't read. The presence of an
// active session is determined by whether `/auth/me` returns a user.
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Only boot in the loading state when there's a session to verify; a visitor
  // with no hint is known-logged-out, so we render immediately (no probe).
  const [isLoading, setIsLoading] = useState(() => hasSessionHint());

  const refresh = useCallback(async () => {
    try {
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
      clearSessionHint();
    }
  }, []);

  useEffect(() => {
    // No session hint → known logged out. Skip the probe (and its 401) entirely.
    if (!hasSessionHint()) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh().finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(async (email: string, password: string, rememberMe = false, captchaToken?: string): Promise<LoginOutcome> => {
    const response = await authApi.login(email, password, rememberMe, captchaToken);
    // An account with a registered passkey/security key gets a challenge, not a
    // session — the caller drives the second factor via completeMfa.
    if ('mfaRequired' in response) {
      return { status: 'mfa_required', mfaToken: response.mfaToken, options: response.authenticationOptions };
    }
    setUser(response.user);
    writeSessionHint();
    return { status: 'ok' };
  }, []);

  const completeMfa = useCallback(async (mfaToken: string, options: PublicKeyCredentialRequestOptionsJSON, rememberMe = false) => {
    const response = await webauthnApi.authenticate(mfaToken, options, rememberMe);
    setUser(response.user);
    writeSessionHint();
  }, []);

  // Public sign-up. On the auto-login path the API returns a user (201); on the
  // silent-duplicate / verify-first path it returns only a message, so we leave
  // the session untouched.
  const register = useCallback(async (payload: RegisterPayload) => {
    const response = await authApi.register(payload);
    if ('user' in response) {
      setUser(response.user);
      writeSessionHint();
    }
  }, []);

  const oauthSignIn = useCallback(async (provider: OauthProvider, token: string, consent?: ConsentFields) => {
    const response = await authApi.oauthCallback(provider, token, consent);
    setUser(response.user);
    writeSessionHint();
  }, []);

  const requestMagicLink = useCallback(async (email: string, captchaToken?: string) => {
    await authApi.requestMagicLink(email, captchaToken);
  }, []);

  const consumeMagicLink = useCallback(async (token: string) => {
    const response = await authApi.consumeMagicLink(token);
    setUser(response.user);
    writeSessionHint();
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    setUser(null);
    clearSessionHint();
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    completeMfa,
    register,
    oauthSignIn,
    requestMagicLink,
    consumeMagicLink,
    logout,
    refresh,
  }), [user, isLoading, login, completeMfa, register, oauthSignIn, requestMagicLink, consumeMagicLink, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
